import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CalendarPlusIcon, CheckIcon, ClockIcon, CreditCardIcon, MapPinIcon, SearchIcon, SparklesIcon, UsersIcon } from 'lucide-react';
import { inscriptionCategories, inscriptionChild, type ActivitySlot } from '../../data/loisirs';
import type { InscriptionKind } from '../../hooks/useInscriptionConversation';
interface InscriptionMessageProps {
  kind: InscriptionKind;
  live: boolean;
  category: string | null;
  slots: ActivitySlot[];
  slot: ActivitySlot | null;
  onPickCategory: (category: string) => void;
  onRegister: (slot: ActivitySlot) => void;
  onRestart: () => void;
}
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
function SlotDetails({
  slot
}: {
  slot: ActivitySlot;
}) {
  return <dl className="mt-2 space-y-1 text-[13px] text-slate-600" data-id="element-1428" data-mp="13ujcjmfddhdk">
      <div className="flex items-center gap-1.5" data-id="element-1429" data-mp="yuetppqzoak6">
        <ClockIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1430" data-mp="yt3v04cehszx" />
        <dt className="sr-only" data-id="element-1431" data-mp="tu91o8gt4m1k">Schedule</dt>
        <dd data-id="element-1432" data-mp="txoef4n0dizt">
          {slot.day} · {slot.time}
        </dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-1433" data-mp="oxsa14aefpjq">
        <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1434" data-mp="k5qzpnwsk8wc" />
        <dt className="sr-only" data-id="element-1435" data-mp="g3x7m40nwd7s">Location</dt>
        <dd data-id="element-1436" data-mp="g4p3lfy68cyl">{slot.place}</dd>
      </div>
      <div className="flex items-center gap-1.5" data-id="element-1437" data-mp="g1j9fmaoqkw0">
        <UsersIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1438" data-mp="b5vppmez2jxw" />
        <dt className="sr-only" data-id="element-1439" data-mp="65zizfew4een">Ages and spots</dt>
        <dd data-id="element-1440" data-mp="67u712lgkmmy">
          {slot.audience} · {slot.spots}
        </dd>
      </div>
    </dl>;
}
export function InscriptionMessage({
  kind,
  live,
  category,
  slots,
  slot,
  onPickCategory,
  onRegister,
  onRestart
}: InscriptionMessageProps) {
  if (kind === 'activite') {
    return <div data-id="element-1441" data-mp="3slf1r6ebbjnp">
        <p className={eyebrow} data-id="element-1442" data-mp="3sl5brit9pci0">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-1443" data-mp="3eoich9duea8x" />
          Registration
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1444" data-mp="3iok3molybl9y">Which activity would you like?</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-1445" data-mp="a98pzp8sfm9s">
          I am looking for open spots for {inscriptionChild.name} ({inscriptionChild.age}).
        </p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1446" data-mp="1hgklsymf3c2">
            {inscriptionCategories.map(option => <li key={option} data-id="element-1447" data-mp="5ia8imc2ci4y">
                <button type="button" onClick={() => onPickCategory(option)} className={chipButton} data-id="element-1448" data-mp="5hh9k7jciwxd">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'resultats') {
    const [best, ...others] = slots;
    if (!best) {
      return <div data-id="element-1449" data-mp="2inf890r98neu">
          <p className={eyebrow} data-id="element-1450" data-mp="2dqf44vdytuqd">
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1451" data-mp="2dr6w7w8ctabw" />
            Search
          </p>
          <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1452" data-mp="2sfl10cpbz8v3">
            No spots found for that activity.
          </p>
          <p className="mt-1 text-sm text-slate-500" data-id="element-1453" data-mp="2ngqizcz6toc1">Name another activity and I will look again.</p>
        </div>;
    }
    return <div data-id="element-1454" data-mp="1urkqjpq8n0bs">
        <p className={eyebrow} data-id="element-1455" data-mp="1us2rv0rrc4qk">
          <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-1456" data-mp="1usun2ahg00gb" />
          {slots.length} spots found in {category}
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-1457" data-mp="29mqtu4xy8l1f">
          Here is what I recommend for {inscriptionChild.name}
        </p>

        <article className="mt-4 rounded-2xl border-2 border-ville-navy bg-ville-sand p-4" data-id="element-1458" data-mp="3l3h3xz5htx0b">
          <div className="flex flex-wrap items-center gap-2" data-id="element-1459" data-mp="3g5fbinm6glc6">
            <span className="rounded-full bg-ville-lime px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-ville-navy" data-id="element-1460" data-mp="3g704w9wr9y8g">
              Recommended
            </span>
            <p className="text-[15px] font-extrabold text-ville-navy" data-id="element-1461" data-mp="1unevmb1r1b41">{best.name}</p>
          </div>
          <SlotDetails slot={best} data-id="element-1462" data-mp="1ymx76g7ok9u7" />
          {best.note && <p className="mt-2 text-[13px] font-semibold text-ville-deep" data-id="element-1463" data-mp="23nbhy60o1xvx">{best.note}</p>}
          <p className="mt-2 text-[13px] font-bold text-ville-navy" data-id="element-1464" data-mp="28mo04dglegs6">{best.price}</p>
          {live && <button type="button" onClick={() => onRegister(best)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 sm:w-auto" data-id="element-1465" data-mp="2dlhhigz02rxm">
              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1466" data-mp="2wd0heexq9pvf" />
              Register {inscriptionChild.name} — one click
            </button>}
        </article>

        {others.length > 0 && <ul className="mt-3 space-y-2" data-id="element-1467" data-mp="31fhsbtztk206">
            {others.map(option => <li key={option.id} data-id="element-1468" data-mp="2wj13exkspjm1">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-1469" data-mp="2wk2gf7ldgg6e">
                  <div className="min-w-0" data-id="element-1470" data-mp="3p6dqd8uvbtfv">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-1471" data-mp="3p65dhgyqwo4o">
                      {option.day} · {option.time}
                    </p>
                    <p className="truncate text-xs text-slate-500" data-id="element-1472" data-mp="3fgu6q0zbzvjy">
                      {option.place} · {option.spots}
                    </p>
                  </div>
                  {live && <button type="button" onClick={() => onRegister(option)} className={chipButton} data-id="element-1473" data-mp="brqyp88q331t">
                      Choose
                    </button>}
                </div>
              </li>)}
          </ul>}
      </div>;
  }
  return <div data-id="element-1474" data-mp="21b8noucsvi7j">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-1475" data-mp="267zb62qxpg2z">
        <CheckIcon className="h-6 w-6" data-id="element-1476" data-mp="1riban3qkulgb" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1477" data-mp="1wi61j7acrb1d">
        {inscriptionChild.name} is registered
      </h3>
      {slot && <>
          <p className="mt-1 text-[15px] font-bold text-ville-navy" data-id="element-1478" data-mp="1ih3gmxv3g6s1">{slot.name}</p>
          <SlotDetails slot={slot} data-id="element-1479" data-mp="1nj2j85j0eznx" />
          <p className="mt-3 flex items-center gap-1.5 text-[13px] text-slate-600" data-id="element-1480" data-mp="18k8g69f9fwb4">
            <CreditCardIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1481" data-mp="18mbinsxlnhwq" />
            {slot.price} · paid with the saved card
          </p>
          <p className="mt-1 text-xs text-slate-500" data-id="element-1482" data-mp="32zb442onv6bx">
            Confirmation sent by email · spot held with the family’s citizen card
          </p>
        </>}
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-1483" data-mp="w97qqzl85aa6">
          <Link to="/my-recreation" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-1484" data-mp="w7myjgmdu3fh">
            View my activities
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1485" data-mp="116zgbmyeckb7" />
          </Link>
          <button type="button" className={`inline-flex items-center gap-2 ${chipButton}`} data-id="element-1486" data-mp="1eyw81pw5ks72">
            <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1487" data-mp="1ayc8kfo3xchr" />
            Add to calendar
          </button>
          <button type="button" onClick={onRestart} className={chipButton} data-id="element-1488" data-mp="1owkcx8nh3g2y">
            Register for something else
          </button>
        </div>}
    </div>;
}